import { useCallback, useState } from "react";
import type { MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadMoreConversations, deleteConversation, renameConversation } from "@redux/Hermes/hermes.action";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import type { AppDispatch, ConversationListItem, HermesRootState } from "@containers/Hermes/types";
import { HistoryListSkeleton } from "./Skeleton";

type RecentPanelProps = {
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  // Rendered inside the history drawer: the drawer supplies the chrome, the
  // header already has "New chat", so only the list remains.
  embedded?: boolean;
};

const TITLE_MAX = 200;

export default function RecentPanel({ onNewChat, onSelectConversation, embedded = false }: RecentPanelProps) {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useHermesAnalytics();
  const conversations = useSelector((state: HermesRootState) => state.hermes.conversations);
  const conversationsLoading = useSelector((state: HermesRootState) => state.hermes.conversationsLoading);
  const conversationsHasMore = useSelector((state: HermesRootState) => state.hermes.conversationsHasMore);
  const activeConversationId = useSelector((state: HermesRootState) => state.hermes.activeConversationId);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreConversations());
  }, [dispatch]);

  const handleDeleteClick = useCallback((e: MouseEvent, id: string) => {
    e.stopPropagation();
    setConfirmingDeleteId(id);
  }, []);

  const handleDeleteCancel = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setConfirmingDeleteId(null);
  }, []);

  const handleDeleteConfirm = useCallback(
    async (e: MouseEvent, id: string) => {
      e.stopPropagation();
      setConfirmingDeleteId(null);
      setDeletingIds((prev) => new Set(prev).add(id));

      const wasActive = id === activeConversationId;
      await dispatch(deleteConversation(id));
      analytics.conversationDeleted({ conversationId: id });

      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      if (wasActive) onNewChat();
    },
    [dispatch, activeConversationId, onNewChat, analytics]
  );

  const handleEditClick = useCallback((e: MouseEvent, conv: ConversationListItem) => {
    e.stopPropagation();
    setConfirmingDeleteId(null);
    setEditingId(conv.id);
    setEditingValue(conv.title || "");
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditingValue("");
  }, []);

  const handleEditSubmit = useCallback(
    async (id: string) => {
      const title = editingValue.trim();
      setEditingId(null);
      setEditingValue("");
      if (!title) return;
      const current = conversations.find((c) => c.id === id);
      if (current && current.title === title) return;
      await dispatch(renameConversation({ conversationId: id, title }));
      analytics.conversationRenamed({ conversationId: id });
    },
    [dispatch, editingValue, conversations, analytics]
  );

  return (
    <div className={embedded ? "tw-flex tw-flex-col tw-h-full tw-min-h-0 tw-overflow-hidden" : "tw-w-[280px] tw-flex-shrink-0 tw-border-l tw-border-gray-200 tw-bg-white tw-flex tw-flex-col tw-h-full tw-overflow-hidden"}>
      <div className="tw-flex-grow tw-overflow-y-auto tw-px-3 tw-py-4">
        {!embedded && (
          <>
            <button onClick={onNewChat} className="tw-mb-4 tw-ps-2 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-medium tw-text-primary-purple-600 hover:tw-text-primary-purple-700 tw-border-none tw-bg-transparent tw-cursor-pointer tw-transition-colors">
              <span className="material-symbols-outlined tw-text-[20px]">add_comment</span>
              Start new chat
            </button>

            <h3 className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mb-4">Recent</h3>
          </>
        )}

        {conversations.length === 0 ? (
          conversationsLoading ? (
            <HistoryListSkeleton />
          ) : (
            <p className="tw-text-xs tw-text-gray-400">No conversations yet</p>
          )
        ) : (
          <ul className="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-1">
            {conversations.map((conv) => {
              const isActive = conv.id === activeConversationId;
              const isConfirming = confirmingDeleteId === conv.id;
              const isDeleting = deletingIds.has(conv.id);
              const isEditing = editingId === conv.id;

              if (isEditing) {
                return (
                  <li key={conv.id}>
                    <input
                      type="text"
                      value={editingValue}
                      maxLength={TITLE_MAX}
                      autoFocus
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={() => handleEditSubmit(conv.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleEditSubmit(conv.id);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          handleEditCancel();
                        }
                      }}
                      aria-label="Conversation title"
                      className="tw-w-full tw-box-border tw-text-sm tw-px-2 tw-py-1.5 tw-rounded-md tw-border tw-border-primary-purple-300 tw-bg-white tw-text-gray-800 focus:tw-outline-none focus:tw-border-primary-purple-500"
                    />
                  </li>
                );
              }

              if (isConfirming) {
                return (
                  <li key={conv.id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded-md tw-bg-error-50">
                    <span className="tw-text-xs tw-text-error-600 tw-font-medium tw-truncate">Confirm Delete?</span>
                    <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                      <button onClick={(e) => handleDeleteConfirm(e, conv.id)} className="tw-text-xs tw-font-semibold tw-text-white tw-bg-error-500 hover:tw-bg-error-600 tw-px-2 tw-py-1 tw-rounded tw-border-none tw-cursor-pointer">
                        Delete
                      </button>
                      <button onClick={handleDeleteCancel} className="tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-px-2 tw-py-1 tw-rounded tw-border-none tw-bg-transparent tw-cursor-pointer">
                        Cancel
                      </button>
                    </div>
                  </li>
                );
              }

              return (
                <li key={conv.id} className={`tw-group tw-relative tw-flex tw-items-center tw-rounded-md tw-transition-colors tw-duration-100 ${isActive ? "tw-bg-primary-purple-50" : "hover:tw-bg-gray-50"} ${isDeleting ? "tw-opacity-40" : ""}`}>
                  <button onClick={() => !isDeleting && onSelectConversation(conv.id)} disabled={isDeleting} title={conv.title || "New conversation"} className={`tw-flex-grow tw-min-w-0 tw-text-left tw-text-sm tw-truncate tw-px-2 tw-py-1.5 tw-border-none tw-bg-transparent tw-cursor-pointer ${isActive ? "tw-text-primary-purple-700 tw-font-medium" : "tw-text-gray-900"} disabled:tw-cursor-not-allowed`}>
                    {conv.title || "New conversation"}
                  </button>

                  <div className={`tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-hidden group-hover:tw-flex tw-items-center tw-gap-0.5 tw-pl-1 tw-pr-1 tw-rounded-md ${isActive ? "tw-bg-primary-purple-50" : "tw-bg-gray-50"}`}>
                    <button onClick={(e) => handleEditClick(e, conv)} disabled={isDeleting} aria-label="Rename conversation" title="Rename" className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded tw-border-none tw-bg-transparent tw-cursor-pointer tw-text-gray-500 hover:tw-text-primary-purple-600">
                      <span className="material-symbols-outlined !tw-text-[16px]">edit</span>
                    </button>
                    <button onClick={(e) => handleDeleteClick(e, conv.id)} disabled={isDeleting} aria-label="Delete conversation" title="Delete" className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded tw-border-none tw-bg-transparent tw-cursor-pointer tw-text-gray-500 hover:tw-text-error-500">
                      <span className="material-symbols-outlined !tw-text-[16px]">delete</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {conversationsHasMore && (
          <button onClick={handleLoadMore} disabled={conversationsLoading} className="tw-mt-2 tw-text-xs tw-text-gray-400 hover:tw-text-primary-purple-600 tw-border-none tw-bg-transparent tw-cursor-pointer disabled:tw-opacity-50">
            {conversationsLoading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}
